import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  TableSortLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import moment from 'moment';
import LoadProgress from '../../common/LoadProgress.js';
import SystemAlert from '../../common/SystemAlert.js';
import axios from 'axios';

export default function FormsList() {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [forms, setForms] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [alert, setAlert] = React.useState(null);

  // Sorting state
  const [sortBy, setSortBy] = React.useState('dateUpdatedStr');
  const [sortDirection, setSortDirection] = React.useState('desc');

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedForms = React.useMemo(() => {
    const sorted = [...forms].sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'name') {
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
      } else if (sortBy === 'dateCreatedStr') {
        aValue = a.date_created || a.dateCreatedStr || '';
        bValue = b.date_created || b.dateCreatedStr || '';
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else if (sortBy === 'dateUpdatedStr') {
        aValue = a.date_updated || a.dateUpdatedStr || '';
        bValue = b.date_updated || b.dateUpdatedStr || '';
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else {
        aValue = '';
        bValue = '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [forms, sortBy, sortDirection]);

  // Handle delete click
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    try {
      await axios.post(`/form/delete?id=${deleteId}`);
      setForms(forms.filter(form => form.id !== deleteId));
      setOpenDialog(false);
      setAlert({ severity: 'success', message: 'Form deleted successfully' });
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      console.error('Error deleting form:', err);
      setAlert({ severity: 'error', message: 'Failed to delete form. This form might be used elsewhere.' });
      setTimeout(() => setAlert(null), 3000);
    }
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle duplicate form
  const handleDuplicate = async (id) => {
    try {
      const response = await axios.post(`/form/duplicate?id=${id}`);
      setForms([...forms, response.data]);
      setAlert({ severity: 'success', message: 'Form duplicated successfully' });
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      console.error('Error duplicating form:', err);
      setAlert({ severity: 'error', message: 'Failed to duplicate form' });
      setTimeout(() => setAlert(null), 3000);
    }
  };

  // Load forms from API on component mount
  React.useEffect(() => {
    const fetchForms = async () => {
      try {
        setLoading(true);
        const response = await axios.post('/form/getAll');
        setForms(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching forms:', err);
        setError('Failed to load forms. Please try again later.');
        setForms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchForms();
  }, []);

  return (
    <div>
      {alert && (
        <SystemAlert open={alert !== null} severity={alert.severity} message={alert.message} />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px' }}>
        <Typography variant="h5" component="h1">
          Forms
        </Typography>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Button
            variant='outlined'
            onClick={() => navigate('/FormInfo')}
          >
            Add New Form
          </Button>
        </div>
      </div>
      {loading ? (
        <LoadProgress open={loading} />
      ) : error ? (
        <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
          {error}
        </div>
      ) : (
        <Table aria-label="forms table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Form Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'dateCreatedStr'}
                  direction={sortBy === 'dateCreatedStr' ? sortDirection : 'asc'}
                  onClick={() => handleSort('dateCreatedStr')}
                >
                  Created On
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'dateUpdatedStr'}
                  direction={sortBy === 'dateUpdatedStr' ? sortDirection : 'asc'}
                  onClick={() => handleSort('dateUpdatedStr')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedForms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No forms found</TableCell>
              </TableRow>
            ) : (
              sortedForms.map((form) => (
                <TableRow
                  key={form.id}
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  <TableCell component="th" scope="row">
                    {form.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {form.createdBy || 'User'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {form.dateCreatedStr || '-'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {form.dateUpdatedStr || '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        sx={{ cursor: 'pointer', color: 'success.main' }}
                        onClick={() => navigate(`/FormInfo/${form.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => handleDuplicate(form.id)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ cursor: 'pointer', color: 'error.main' }}
                        onClick={() => handleDeleteClick(form.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this form and the form fields? <br></br>This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDelete} color="error" autoFocus variant='contained'>
            Delete
          </Button>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}